import API from './config';

// ─── Auth ────────────────────────────────────────────────────────────────────
export const authService = {
  registerClient: (data) => API.post('/auth/register/client', data),
  registerExpert: (data) => API.post('/auth/register/expert', data),
  getCohorts: () => API.get('/public/cohorts'),
  login: (data) => API.post('/auth/login', data),
  me: () => API.get('/auth/me'),
  refreshToken: (refreshToken) => API.post('/auth/refresh-token', { refreshToken }),
  verifyEmail: (userId, token) => API.post('/auth/verify-email', { userId, token }),
  forgotPassword: (email) => API.post('/auth/forgot-password', { email }),
  resetPassword: (userId, token, newPassword) => API.post('/auth/reset-password', { userId, token, newPassword }),
  changePassword: (currentPassword, newPassword) => API.post('/auth/change-password', { currentPassword, newPassword }),
  logout: (refreshToken) => API.post('/auth/logout', { refreshToken }),
  logoutAll: () => API.post('/auth/logout-all'),

  forgotPasswordByPhone: (phone)              => API.post('/auth/forgot-password-phone', { phone }),
  resetPasswordByOtp:    (phone, otp, newPassword) => API.post('/auth/reset-password-otp', { phone, otp, newPassword }),
  resetPasswordByOtpEmail: (email, otp, newPassword) => API.post('/auth/reset-password-otp-email', { email, otp, newPassword }),
};

// ─── Users ───────────────────────────────────────────────────────────────────
export const userService = {
  getProfile: () => API.get('/users/profile'),
  updateProfile: (data) => API.patch('/users/profile', data),
  updateClientBusiness: (data) => API.patch('/users/profile/client', data),
  uploadAvatar: (formData) => API.post('/users/avatar', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteAvatar: () => API.delete('/users/avatar'),
  getUserById: (id) => API.get(`/users/${id}`),
};

// ─── Experts ─────────────────────────────────────────────────────────────────
export const expertService = {
  browse: (params) => API.get('/experts', { params }),
  getPublicProfile: (expertProfileId) => API.get(`/experts/${expertProfileId}`),
  getAvailability: (expertProfileId) => API.get(`/experts/${expertProfileId}/availability`),
  // Expert-auth routes
  getMyDashboard: () => API.get('/experts/me/dashboard'),
  getMyProfile: () => API.get('/experts/me/profile'),
  updateMyProfile: (data) => API.patch('/experts/me/profile', data),
  uploadAvatar: (formData) => API.post('/experts/me/avatar', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  uploadIntroVideo: (formData) => API.post('/experts/me/intro-video', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateLevels: (data) => API.patch('/experts/me/levels', data),
  updateAvailability: (data) => API.patch('/experts/me/availability', data),
  getMySessions: (params) => API.get('/experts/me/sessions', { params }),
  getMyTimesheet: (params) => API.get('/experts/me/timesheet', { params }),
  downloadMyTimesheet: (params) => API.get('/experts/me/timesheet/download', { params, responseType: 'blob' }),
  getMyBookings: (params) => API.get('/experts/me/bookings', { params }),
  respondToBooking: (bookingId, action, reason) => API.post(`/experts/me/bookings/${bookingId}/respond`, { action, reason }),
  postSessionAsContent: (sessionId, data) => API.post(`/experts/me/sessions/${sessionId}/post`, data),
  submitReview: (sessionId, data) => API.post(`/experts/sessions/${sessionId}/review`, data),
  getReviews: (id) => API.get(`/experts/${id}/reviews`),
};

// ─── Levels ──────────────────────────────────────────────────────────────────
export const levelService = {
  getAll: () => API.get('/levels'),
  getById: (id) => API.get(`/levels/${id}`),
  getBySlug: (slug) => API.get(`/levels/slug/${slug}`),
  getExpertsForLevel: (levelId, params) => API.get(`/levels/${levelId}/experts`, { params }),
};

// ─── Stages ──────────────────────────────────────────────────────────────────
export const stageService = {
  getForLevel: (levelId) => API.get(`/stages/level/${levelId}`),
  getById: (stageId) => API.get(`/stages/${stageId}`),
};

// ─── Content ─────────────────────────────────────────────────────────────────
export const contentService = {
  getInStage: (stageId) => API.get(`/content/stage/${stageId}`),
  getById: (contentId) => API.get(`/content/${contentId}`),
  getStreamUrl: (contentId) => API.get(`/content/${contentId}/stream`),
  uploadVideo: (stageId, formData) => API.post(`/content/stage/${stageId}/upload/video`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  uploadDocument: (stageId, formData) => API.post(`/content/stage/${stageId}/upload/document`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateMetadata: (contentId, data) => API.patch(`/content/${contentId}`, data),
  delete: (contentId) => API.delete(`/content/${contentId}`),
};

// ─── Bookings ─────────────────────────────────────────────────────────────────
export const bookingService = {
  create: (data) => API.post('/bookings', data),
  getAll: (params) => API.get('/bookings', { params }),
  getMyTimesheet: (params) => API.get('/bookings/my/timesheet', { params }),
  downloadMyTimesheet: (params) => API.get('/bookings/my/timesheet/download', { params, responseType: 'blob' }),
  getById: (id) => API.get(`/bookings/${id}`),
  cancel: (id, reason) => API.post(`/bookings/${id}/cancel`, { reason }),
  reschedule: (id, scheduledAt) => API.post(`/bookings/${id}/reschedule`, { scheduledAt }),
  getAllAdmin: (params) => API.get('/bookings/admin/all', { params }),
};

// ─── Sessions ─────────────────────────────────────────────────────────────────
export const sessionService = {
  createFromBooking: (bookingId) => API.post(`/sessions/from-booking/${bookingId}`),
  getById: (id) => API.get(`/sessions/${id}`),
  getJoinToken: (id) => API.get(`/sessions/${id}/join-token`),
  getChatHistory: (id) => API.get(`/sessions/${id}/chat`),
  start: (id) => API.post(`/sessions/${id}/start`),
  end: (id) => API.post(`/sessions/${id}/end`),
  markComplete: (id) => API.post(`/sessions/${id}/complete`),
  retryTranscript: (id) => API.post(`/sessions/${id}/transcript/retry`),
  getMyCompleted: (params) => API.get('/sessions/my/completed', { params }),
};

// ─── Transcripts ──────────────────────────────────────────────────────────────
export const transcriptService = {
  get: (sessionId) => API.get(`/transcripts/${sessionId}`),
  download: (sessionId) => API.get(`/transcripts/${sessionId}/download`),
  process: (sessionId) => API.post(`/transcripts/${sessionId}/process`),
  retry: (sessionId) => API.post(`/transcripts/${sessionId}/retry`),
  generateChat: (sessionId) => API.post(`/transcripts/${sessionId}/generate-chat`),
};

// ─── Canvas ───────────────────────────────────────────────────────────────────
export const canvasService = {
  getForLevel: (levelId) => API.get(`/canvas/level/${levelId}`),
  submit: (formData) => API.post('/canvas/submit', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getMySubmission: (canvasActivityId) => API.get(`/canvas/submission/${canvasActivityId}`),
  getSubmissionsToGrade: (params) => API.get('/canvas/submissions', { params }),
  gradeSubmission: (submissionId, data) => API.post(`/canvas/submissions/${submissionId}/grade`, data),
  getSubmissionAdmin:  (id)       => API.get(`/canvas/admin/submissions/${id}`),
  gradeSubmissionAdmin: (id, data) => API.post(`/canvas/admin/submissions/${id}/grade`, data),
};

// ─── Progress ─────────────────────────────────────────────────────────────────
export const progressService = {
  getFull: () => API.get('/progress'),
  getAllLevels: () => API.get('/progress/levels'),
  getLevelProgress: (levelId) => API.get(`/progress/levels/${levelId}`),
  updateContentProgress: (contentId, data) => API.post(`/progress/content/${contentId}`, data),
  markContentComplete: (contentId) => API.post(`/progress/content/${contentId}/complete`),
  advance: () => API.post('/progress/advance'),
  getLevelProgress: (levelId) => API.get(`/progress/levels/${levelId}`),
  markComplete: (contentId) => API.post(`/progress/content/${contentId}/complete`),
};

// ─── Notifications ────────────────────────────────────────────────────────────
export const notificationService = {
  getAll: (params) => API.get('/notifications', { params }),
  getUnreadCount: () => API.get('/notifications/unread-count'),
  markRead: (id) => API.patch(`/notifications/${id}/read`),
  markAllRead: () => API.patch('/notifications/read-all'),
  delete: (id) => API.delete(`/notifications/${id}`),
};

// ─── Communities ─────────────────────────────────────────────────────────────
export const communityService = {
  create:        (data)              => API.post('/communities', data),
  getMy:         ()                  => API.get('/communities/my'),
  getById:       (id)                => API.get(`/communities/${id}`),
  getMessages:   (id, params)        => API.get(`/communities/${id}/messages`, { params }),
  sendMessage:   (id, data)          => API.post(`/communities/${id}/messages`, data),
  invite:        (id, userIds)       => API.post(`/communities/${id}/invite`, { userIds }),
  leave:         (id)                => API.post(`/communities/${id}/leave`),
   uploadFile:   (id, formData)      => API.post(`/communities/${id}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getCallToken:  (id)                => API.get(`/communities/${id}/call-token`),
  searchUsers:   (communityId, q)    => API.get('/communities/search-users', { params: { communityId, q } }),
  adminGetAll:   (params)            => API.get('/communities/admin/all', { params }),
};

// ─── Communities ─────────────────────────────────────────────────────────────
// export const communityService = {
//   create:       (data)              => API.post('/communities', data),
//   getMy:        ()                  => API.get('/communities/my'),
//   getById:      (id)                => API.get(`/communities/${id}`),
//   getMessages:  (id, params)        => API.get(`/communities/${id}/messages`, { params }),
//   sendMessage:  (id, data)          => API.post(`/communities/${id}/messages`, data),
//   invite:       (id, userIds)       => API.post(`/communities/${id}/invite`, { userIds }),
//   leave:        (id)                => API.post(`/communities/${id}/leave`),
//   uploadFile:   (id, formData)      => API.post(`/communities/${id}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
//   getCallToken: (id)                => API.get(`/communities/${id}/call-token`),
//   searchUsers:  (communityId, q)    => API.get('/communities/search-users', { params: { communityId, q } }),
// };

// ─── Seminars ─────────────────────────────────────────────────────────────────
export const seminarService = {
  // Public — no auth needed
  getCarousel:      ()           => API.get('/seminars/public/carousel'),
  getPublic:        ()           => API.get('/seminars/public'),
  joinByCode:       (code)       => API.get(`/seminars/public/join/${code}`),
  // Admin
  getAll:           (params)     => API.get('/seminars', { params }),
  create:           (data)       => API.post('/seminars', data),
  update:           (id, data)   => API.patch(`/seminars/${id}`, data),
  remove:           (id)         => API.delete(`/seminars/${id}`),
  getFeatured:      ()           => API.get('/seminars/featured'),
  toggleFeatured:   (userId, data) => API.post(`/seminars/featured/${userId}`, data),
  updateFeatured:   (userId, data) => API.patch(`/seminars/featured/${userId}`, data),
  searchUsers:      (q)            => API.get('/seminars/users/search', { params: { q } }),
  regenerateRoom:   (id, url)       => API.post(`/seminars/${id}/regenerate-room`, { customRoomUrl: url || undefined }),
  enableRecording:  (id)            => API.post(`/seminars/${id}/enable-recording`),
};

// ─── Group Sessions ──────────────────────────────────────────────────────────
export const groupSessionService = {
  create:          (data)       => API.post('/group-sessions', data),
  joinByCode:      (joinCode)   => API.post('/group-sessions/join', { joinCode }),
  getMySessions:   (params)     => API.get('/group-sessions/my', { params }),
  getById:         (id)         => API.get(`/group-sessions/${id}`),
  getJoinToken:    (id)         => API.get(`/group-sessions/${id}/token`),
  complete:        (id)         => API.post(`/group-sessions/${id}/complete`),
  cancel:          (id)         => API.post(`/group-sessions/${id}/cancel`),
  // Admin
  adminGetAll:     (params)     => API.get('/group-sessions/admin/all', { params }),
  getSettings:     ()           => API.get('/group-sessions/admin/settings'),
  updateSetting:   (key, value) => API.patch(`/group-sessions/admin/settings/${key}`, { value }),
};

// ─── Attendance (Daily.co roll-call + call quality) ───────────────────────────
export const attendanceService = {
  getForSession:      (id) => API.get(`/sessions/${id}/attendance`),
  getForGroupSession: (id) => API.get(`/group-sessions/admin/${id}/attendance`),
  getForSeminar:      (id) => API.get(`/seminars/${id}/attendance`),
  syncSession:        (id) => API.post(`/sessions/${id}/attendance/sync`),
  syncGroupSession:   (id) => API.post(`/group-sessions/admin/${id}/attendance/sync`),
  syncSeminar:        (id) => API.post(`/seminars/${id}/attendance/sync`),
};

// ─── Cohorts (Classrooms, Lectures, Assignments & Members) ────────────────────
export const cohortService = {
  getAll:                 (params)         => API.get('/cohorts', { params }),
  getMyCohorts:           ()               => API.get('/cohorts/my-cohorts'),
  create:                 (data)           => API.post('/cohorts', data),
  update:                 (id, data)       => API.patch(`/cohorts/${id}`, data),
  remove:                 (id)             => API.delete(`/cohorts/${id}`),
  getById:                (id)             => API.get(`/cohorts/${id}`),
  searchAvailable:        (id, q)          => API.get(`/cohorts/${id}/available-users`, { params: { q } }),
  searchAvailableExperts: (id, q)          => API.get(`/cohorts/${id}/available-experts`, { params: { q } }),
  addMembers:             (id, userIds, role) => API.post(`/cohorts/${id}/members`, { userIds, role }),
  assignInstructor:       (id, data)       => API.post(`/cohorts/${id}/instructors`, data),
  removeMember:           (id, userId)     => API.delete(`/cohorts/${id}/members/${userId}`),

  // Online Lectures
  getLectures:            (id)             => API.get(`/cohorts/${id}/lectures`),
  createLecture:          (id, data)       => API.post(`/cohorts/${id}/lectures`, data),
  getLectureById:         (id, lectureId)  => API.get(`/cohorts/${id}/lectures/${lectureId}`),
  deleteLecture:          (id, lectureId)  => API.delete(`/cohorts/${id}/lectures/${lectureId}`),
  joinLecture:            (id, lectureId)  => API.post(`/cohorts/${id}/lectures/${lectureId}/join`),
  leaveLecture:           (id, lectureId, data) => API.post(`/cohorts/${id}/lectures/${lectureId}/leave`, data),
  heartbeatLecture:       (id, lectureId, data) => API.post(`/cohorts/${id}/lectures/${lectureId}/heartbeat`, data),
  getLectureAttendance:   (id, lectureId)  => API.get(`/cohorts/${id}/lectures/${lectureId}/attendance`),

  // Coursework / Assignments
  getAssignments:         (id)             => API.get(`/cohorts/${id}/assignments`),
  createAssignment:       (id, data)       => API.post(`/cohorts/${id}/assignments`, data),
  submitAssignment:       (id, assignmentId, formData) => API.post(`/cohorts/${id}/assignments/${assignmentId}/submit`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getSubmissions:         (id, assignmentId) => API.get(`/cohorts/${id}/assignments/${assignmentId}/submissions`),
  gradeSubmission:        (id, assignmentId, submissionId, data) => API.patch(`/cohorts/${id}/assignments/${assignmentId}/submissions/${submissionId}/grade`, data),
};

// ─── Admin ────────────────────────────────────────────────────────────────────
export const adminService = {
  getDashboard: () => API.get('/admin/dashboard'),
  // Experts
  getAllExperts: (params) => API.get('/admin/experts', { params }),
  getPendingExperts: (params) => API.get('/admin/experts/pending', { params }),
  getExpertDetail: (id) => API.get(`/admin/experts/${id}`),
  markReviewing: (id) => API.post(`/admin/experts/${id}/review`),
  approveExpert: (id, notes) => API.post(`/admin/experts/${id}/approve`, { notes }),
  rejectExpert: (id, notes) => API.post(`/admin/experts/${id}/reject`, { notes }),
  // Levels
  getAllLevels: () => API.get('/admin/levels'),
  createLevel: (data) => API.post('/admin/levels', data),
  updateLevel: (id, data) => API.patch(`/admin/levels/${id}`, data),
  disableExpert:      (id) => API.patch(`/admin/experts/${id}/disable`),
  enableExpert:       (id) => API.patch(`/admin/experts/${id}/enable`),
  toggleOnlineStatus: (id) => API.patch(`/admin/experts/${id}/online-status`),
  deleteExpert:       (id) => API.delete(`/admin/experts/${id}`),
  reorderLevels: (orderedIds) => API.patch('/admin/levels/reorder', { orderedIds }),
  deleteLevel: (id) => API.delete(`/admin/levels/${id}`),
  toggleLevelStatus: (id) => API.patch(`/admin/levels/${id}/toggle-status`),

  setExpertRate: (id, data) => API.post(`/reporting/rates/${id}`, data),
  getEarningsSummary: (params) => API.get('/reporting/earnings', { params }),
  getImpactReport: (params) => API.get('/reporting/impact', { params }),
  getPaymentRuns: () => API.get('/reporting/payment-runs'),
  createPaymentRun: (data) => API.post('/reporting/payment-runs', data),
  approvePaymentRun: (id, data) => API.post(`/reporting/payment-runs/${id}/approve`, data),


  // Stages
  getStages: (levelId) => API.get(`/admin/levels/${levelId}/stages`),
  getStagesForLevel: (levelId) => API.get(`/admin/levels/${levelId}/stages`),
  createStage: (levelId, data) => API.post(`/admin/levels/${levelId}/stages`, data),
  // updateStage: (stageId, data) => API.patch(`/admin/stages/${stageId}`, data),
  updateStage: (id, data) => API.patch(`/admin/stages/${id}`, data),
  reorderStages: (levelId, orderedIds) => API.patch(`/admin/levels/${levelId}/stages/reorder`, { orderedIds }),
  // deleteStage: (stageId) => API.delete(`/admin/stages/${stageId}`),
  deleteStage: (id) => API.delete(`/admin/stages/${id}`),

  // Canvas
  createCanvas: (levelId, data) => API.post(`/admin/levels/${levelId}/canvas`, data),
  updateCanvas: (canvasActivityId, data) => API.patch(`/admin/canvas/${canvasActivityId}`, data),
  getAllSubmissions: (params) => API.get('/admin/submissions', { params }),
  gradeSubmission: (submissionId, data) => API.post(`/admin/submissions/${submissionId}/grade`, data),

  // Canvas Activities
  getStageCanvas: (stageId) => API.get(`/admin/stages/${stageId}/canvas`),
  createCanvasActivity: (stageId, data) => API.post(`/admin/stages/${stageId}/canvas`, data),
  updateCanvasActivity: (id, data) => API.patch(`/admin/canvas/${id}`, data),
  deleteCanvasActivity: (id) => API.delete(`/admin/canvas/${id}`),


  // Users
  getAllClients: (params) => API.get('/admin/clients', { params }),
  deleteClient:  (id) => API.delete(`/admin/clients/${id}`),
  suspendUser: (id, reason) => API.post(`/admin/users/${id}/suspend`, { reason }),
  reactivateUser: (id) => API.post(`/admin/users/${id}/reactivate`),
  promoteClientToExpert: (id, profileData = {}) => API.post(`/admin/clients/${id}/promote-to-expert`, profileData),
  getAllSessions: (params) => API.get('/sessions/admin/all', { params }),
  getTimesheetData: (expertProfileId, params) => API.get(`/reporting/timesheet/${expertProfileId}`, { params }),
  downloadTimesheet: (expertProfileId, params) => API.get(`/reporting/timesheet/${expertProfileId}/download`, { params, responseType: 'blob' }),
  // Documents
  getAllDocuments: (params) => API.get('/documents/admin/all', { params }),
  uploadDocument: (formData) => API.post('/documents', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateDocument: (id, data) => API.patch(`/documents/${id}`, data),
  deleteDocument: (id) => API.delete(`/documents/${id}`),
  // Broadcast
  getBroadcastAudience: (params) => API.get('/broadcast/audience', { params }),
  sendBroadcast:        (data)   => API.post('/broadcast/send', data),
  sendToIndividuals:    (data)   => API.post('/broadcast/send-to', data),
  sendToCohort:         (data)   => API.post('/broadcast/send-to-cohort', data),
  retryBroadcast:       (data)   => API.post('/broadcast/retry', data),
  getBroadcastLogs:     (params) => API.get('/broadcast/logs', { params }),
  getBroadcastDetail:   (id)     => API.get(`/broadcast/logs/${id}`),
  searchBroadcastUsers: (params) => API.get('/broadcast/search', { params }),
  // Audit logs
  getAuditLogs: (params) => API.get('/admin/audit-logs', { params }),
};

export const documentService = {
  getAll: (params) => API.get('/documents', { params }),
};

export const assessmentService = {
  getAssessment:      ()          => API.get('/assessment'),
  getMySubmission:    ()          => API.get('/assessment/my-submission'),
  submit:             (data)      => API.post('/assessment/submit', data),
  getSubmissions:     (params)    => API.get('/assessment/submissions', { params }),
  reviewSubmission:   (id, data)  => API.patch(`/assessment/submissions/${id}/review`, data),
  rejectSubmission:   (id, data)  => API.patch(`/assessment/submissions/${id}/reject`, data),
  getAreas:           ()          => API.get('/assessment/areas'),
  createArea:         (data)      => API.post('/assessment/areas', data),
  updateArea:         (id, data)  => API.patch(`/assessment/areas/${id}`, data),
  deleteArea:         (id)        => API.delete(`/assessment/areas/${id}`),
  createQuestion:     (areaId, data) => API.post(`/assessment/areas/${areaId}/questions`, data),
  updateQuestion:     (id, data)  => API.patch(`/assessment/questions/${id}`, data),
  deleteQuestion:     (id)        => API.delete(`/assessment/questions/${id}`),
};


export const publicService = {
  getStats: () => API.get('/public/stats', { withCredentials: false }),
   getSpecializations: () => API.get('/public/specializations'),
};

export const completedProjectService = {
  // Public — no auth needed
  getAll: () => API.get('/completed-projects'),

  // Admin
  getAllAdmin: () => API.get('/completed-projects/admin/all'),
  create: (formData) => API.post('/completed-projects', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, data) => API.patch(`/completed-projects/${id}`, data),
  delete: (id) => API.delete(`/completed-projects/${id}`),
};